/**
 * AppWrapper - Device-based App Switcher
 * 
 * This component automatically detects the device type and renders
 * either the desktop App or the mobile MobileApp.
 * 
 * The desktop code remains completely untouched - this wrapper simply
 * chooses which app to render based on the device.
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { trackPageView } from '@/lib/meta-pixel';

// Desktop App (existing, unchanged)
import App from './App';

// Mobile App (new)
import MobileApp from './mobile/App-mobile';

/**
 * Detect device type reliably.
 * Uses Math.min(window.innerWidth, screen.width) because on real phones
 * window.innerWidth can be ~980px before the viewport meta tag is processed,
 * but screen.width is always correct.
 */
function detectDevice(): 'mobile' | 'desktop' {
  const w = Math.min(window.innerWidth, screen.width);
  const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const mobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i.test(
    navigator.userAgent
  );
  const isMobile = w < 768 || (touch && mobileUA && w < 1024);
  return isMobile ? 'mobile' : 'desktop';
}

/**
 * Inner wrapper that detects the device and renders the right app.
 * Detection runs in useEffect (after paint) so the viewport meta tag
 * has been processed. A second check at 50ms catches edge cases.
 * Once determined, the result is locked for the lifetime of the component.
 */
const AppSwitcher: React.FC = () => {
  const [deviceType, setDeviceType] = useState<'mobile' | 'desktop' | null>(null);

  useEffect(() => {
    // First detection - viewport should be ready after first paint
    const first = detectDevice();
    setDeviceType(first);
    console.log(`📱 Device (initial): ${first.toUpperCase()} (innerW=${window.innerWidth}, screenW=${screen.width})`);

    // Second detection after a short delay to catch late viewport init
    const timer = setTimeout(() => {
      const second = detectDevice();
      if (second !== first) {
        console.log(`📱 Device (corrected): ${second.toUpperCase()} (innerW=${window.innerWidth}, screenW=${screen.width})`);
        setDeviceType(second);
      }
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  // Show nothing until detection completes (typically <1 frame)
  if (deviceType === null) {
    return null;
  }

  if (deviceType === 'mobile') {
    return <MobileApp />;
  }

  return <AppContent />;
};

/**
 * Desktop App Content (extracted from App.tsx structure)
 * This mirrors the desktop App's route structure
 */
import VideoUploader from "./components/video-uploader";
import BriefingVideoUploader from "./components/briefing-video-uploader";
import VideoResult from "@/components/video-result";
import PublicCampaignResult from "@/components/public-campaign-result";
import FreeCheckResult from "@/components/free-check-result";
import VideoShare from "@/components/video-share";
import SelectionResult from "@/components/selection-result";
import Auth from "@/components/auth";
import EmailVerification from "@/components/email-verification";
import ResetPassword from "@/components/reset-password";
import ApiPage from "@/components/api-page";
import TermsOfService from "@/components/terms-of-service";
import PrivacyPolicy from "@/components/privacy-policy";
import Imprint from "@/components/imprint";
import ProtectedRoute from "@/components/protected-route";
import BrandProtectedRoute from "@/components/brand-protected-route";
import BrandCampaignManager from "@/components/brand-campaign-manager";
import CampaignLoader from "@/components/campaign-loader";
import SubscriptionPage from "@/components/subscription-page";
import SubscriptionSuccess from "@/components/subscription-success";
import SubscriptionCancel from "@/components/subscription-cancel";
import { Routes, Route, Navigate } from "react-router-dom";

function AppContent() {
  return (
    <>
      <Routes>
        <Route path="/" element={<Auth />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/verify" element={<EmailVerification />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/api" element={<ApiPage />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/imprint" element={<Imprint />} />
        
        {/* Creator Routes */}
        <Route
          path="/upload"
          element={
            <ProtectedRoute>
              <BriefingVideoUploader />
            </ProtectedRoute>
          }
        />
        <Route
          path="/upload-briefing"
          element={
            <ProtectedRoute>
              <BriefingVideoUploader />
            </ProtectedRoute>
          }
        />
        <Route
          path="/result/:unique_id"
          element={
            <ProtectedRoute>
              <VideoResult />
            </ProtectedRoute>
          }
        />
        <Route path="/share/:selection_id" element={<VideoShare />} />
        <Route
          path="/selection-result/:selection_id"
          element={
            <ProtectedRoute>
              <SelectionResult />
            </ProtectedRoute>
          }
        />
        
        {/* Brand Portal Routes - Agency workflow */}
        <Route
          path="/brand/campaign"
          element={
            <BrandProtectedRoute>
              <BrandCampaignManager />
            </BrandProtectedRoute>
          }
        />
        
        {/* Subscription Routes - No subscription required to access these */}
        <Route
          path="/subscription"
          element={
            <BrandProtectedRoute requireSubscription={false}>
              <SubscriptionPage />
            </BrandProtectedRoute>
          }
        />
        <Route
          path="/subscription/success"
          element={
            <BrandProtectedRoute requireSubscription={false}>
              <SubscriptionSuccess />
            </BrandProtectedRoute>
          }
        />
        <Route
          path="/subscription/cancel"
          element={
            <BrandProtectedRoute requireSubscription={false}>
              <SubscriptionCancel />
            </BrandProtectedRoute>
          }
        />
        
        {/* Public Creator Campaign Upload (no auth required) */}
        <Route path="/upload/:campaign_uuid" element={<CampaignLoader />} />
        <Route path="/video-upload" element={<VideoUploader />} />
        {/* Public campaign results - dedicated component with same layout, always shows buttons */}
        <Route path="/campaign-result/:unique_id" element={<PublicCampaignResult />} />
        {/* Free video check results (public, no auth) */}
        <Route path="/free-check/:check_id" element={<FreeCheckResult />} />
        
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

/**
 * Main AppWrapper component
 * Wraps everything in Router and handles device detection
 */
const MetaPixelPageView: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView();
  }, [location.pathname]);
  return null;
};

const AppWrapper: React.FC = () => {
  return (
    <HelmetProvider>
      <Router>
        <MetaPixelPageView />
        <AppSwitcher />
      </Router>
    </HelmetProvider>
  );
};

export default AppWrapper;
